import type { AttributionReport } from '@/api/attribution'
import {
  changeTone,
  formatChange,
  formatCurrency,
  formatInteger,
  formatPercent,
  formatRatio,
  majorUnits,
  percentChange,
  type Polarity,
} from '@/design'

import { unattributedMetrics, unattributedShare } from './definitions'

/** The page's "no figure" mark: a null is never rendered as a zero. */
export const NO_VALUE = '—'

export function money(minor: number | null, currency: string): string {
  return minor === null ? NO_VALUE : formatCurrency(majorUnits(minor, currency), { currency })
}

export function count(value: number | null): string {
  return value === null ? NO_VALUE : formatInteger(value)
}

export function share(fraction: number | null): string {
  return fraction === null ? NO_VALUE : formatPercent(fraction)
}

export function ratio(value: number | null): string {
  return value === null ? NO_VALUE : formatRatio(value)
}

/** A previous-period change as text and tone; "—" when there is nothing to compare against. */
export function delta(current: number, previous: number, polarity: Polarity = 'up_good') {
  const change = percentChange(current, previous)
  return { text: change === null ? NO_VALUE : formatChange(change), tone: changeTone(change, polarity) }
}

/**
 * The unattributed share in words — "20.2% of revenue", then "£203.00 · 11 of 48 purchases
 * (22.9%)" — for every place it is stated: the Not tracked row's once the report splits it
 * out, null when the API sent no such row (nothing is stated in its place).
 */
export function unattributedShareText(report: AttributionReport): { revenue: string; detail: string } | null {
  const shares = unattributedShare(report)
  const unattributed = unattributedMetrics(report)
  if (!shares || !unattributed) return null
  return {
    revenue: `${share(shares.revenue)} of revenue`,
    detail: `${money(unattributed.revenueMinor, report.currency)} · ${count(unattributed.purchases)} of ${count(report.totals.purchases)} purchases (${share(shares.purchases)})`,
  }
}
